import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, TextField, MenuItem, IconButton, Tooltip, CircularProgress, Chip,
  Paper, Pagination, ToggleButton, ToggleButtonGroup, Link, ListSubheader,
} from '@mui/material';
import {
  Clear, InfoOutlined, ViewList, ViewKanban, DateRange, CheckCircle, Print, Visibility, Edit, Add, Lock,
} from '@mui/icons-material';
import TherapyPeriodsModal from './modals/TherapyPeriodsModal';
import TherapyGoalDetailModal from './modals/TherapyGoalDetailModal';
import TherapyGoalFormModal from './modals/TherapyGoalFormModal';
import { Toast } from './Toast';
import { useToast } from '../hooks/useToast';
import {
  patientTherapyPlanService, therapyPlanErrorMessage,
  TherapyGoalRow, TherapyPeriodRow, TherapyStageRow, TherapyFilterOption,
} from '../services/patientTherapyPlanService';
import { colors, typography } from '../theme/designSystem';
import { filterFieldSx, searchFieldSx, filterBarSx, clearFilterButtonSx, filterActionButtonSx } from '../theme/filterStyles';

/**
 * Aba Plano Terapêutico (RN-541): os PERÍODOS de tratamento e, dentro deles, os OBJETIVOS.
 *
 * Duas visões do mesmo dado (RN-541b): a LISTA, paginada, com a régua de cada objetivo; e o
 * QUADRO, com uma coluna por fase, no mesmo desenho do funil do CRM para o sistema não ter
 * dois kanbans diferentes. Arrastar um card muda a fase — e a fase vira o status do objetivo,
 * gravado no Bubble primeiro (RN-302): se ele recusar, o card volta para onde estava.
 *
 * Cada objetivo declara ESTADO ATUAL e ALVO, de 0 a 10. O atingimento não se digita aqui —
 * o profissional aponta o objetivo na evolução e registra a nota da sessão.
 */
interface Props {
  patientId: string;
}

const SITUACAO: Record<string, { label: string; bg: string; color: string }> = {
  vigente: { label: 'Vigente', bg: '#d4edda', color: '#155724' },
  futuro: { label: 'Futuro', bg: '#e3f2fd', color: '#0d47a1' },
  encerrado: { label: 'Encerrado', bg: '#e9ecef', color: '#495057' },
};

const PRIORIDADE: Record<string, string> = {
  Urgente: '#dc3545',
  Alta: '#fd7e14',
  Média: '#ffc107',
  Baixa: '#6c757d',
};

const PAGE_SIZE = 10;

const formatarData = (iso: string | null): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

/**
 * Texto longo cortado em 2 linhas com "Ler mais" (pedido do Hilton, 02/09): a justificativa
 * de um objetivo passa de 400 caracteres e empurrava a régua para fora da tela.
 */
const ClampedText: React.FC<{ label?: string; text: string; size?: string }> = ({ label, text, size = '0.82rem' }) => {
  const [aberto, setAberto] = useState(false);
  const longo = text.length > 120 || text.includes('\n');

  return (
    <Typography component="div" sx={{ fontSize: size, color: colors.textSecondary, mb: 0.25 }}>
      {label && <strong>{label} </strong>}
      <Box
        component="span"
        sx={
          aberto || !longo
            ? { whiteSpace: 'pre-wrap' }
            : {
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                whiteSpace: 'pre-wrap',
              }
        }
      >
        {text}
      </Box>
      {longo && (
        <Link
          component="button"
          type="button"
          onClick={(e) => { e.stopPropagation(); setAberto(a => !a); }}
          sx={{ fontSize: '0.78rem', color: colors.primary, ml: 0.25, verticalAlign: 'baseline' }}
        >
          {aberto ? 'Ler menos' : 'Ler mais'}
        </Link>
      )}
    </Typography>
  );
};

/** Régua de 0 a 10: onde o objetivo parte, aonde deve chegar e a última nota das evoluções. */
const ScaleBar: React.FC<{ current: number | null; target: number | null; last: number | null }> =
  ({ current, target, last }) => {
    if (current === null && target === null) return null;
    const pct = (v: number) => `${Math.min(Math.max(v, 0), 10) * 10}%`;
    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ position: 'relative', height: 8, borderRadius: 4, backgroundColor: '#e9ecef' }}>
          {current !== null && target !== null && target > current && (
            <Box
              sx={{
                position: 'absolute', left: pct(current), top: 0, bottom: 0,
                width: `${(target - current) * 10}%`,
                backgroundColor: 'rgba(3, 180, 198, 0.18)', borderRadius: 4,
              }}
            />
          )}
          {last !== null && (
            <Box
              sx={{
                position: 'absolute', left: 0, top: 0, bottom: 0, width: pct(last),
                backgroundColor: colors.primary, borderRadius: 4,
              }}
            />
          )}
          {current !== null && (
            <Box
              sx={{
                position: 'absolute', left: pct(current), top: -3, width: 2, height: 14,
                backgroundColor: colors.textSecondary, transform: 'translateX(-1px)',
              }}
            />
          )}
          {target !== null && (
            <Box
              sx={{
                position: 'absolute', left: pct(target), top: -3, width: 2, height: 14,
                backgroundColor: colors.success, transform: 'translateX(-1px)',
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.75 }}>
          <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary }}>
            Estado atual: <strong style={{ color: colors.textPrimary }}>{current ?? '—'}</strong>
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary }}>
            Meta: <strong style={{ color: colors.success }}>{target ?? '—'}</strong>
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary }}>
            Última avaliação:{' '}
            <strong style={{ color: last === null ? colors.textSecondary : colors.primary }}>
              {last ?? 'sem registro'}
            </strong>
          </Typography>
        </Box>
      </Box>
    );
  };

const GoalCard: React.FC<{
  goal: TherapyGoalRow;
  onOpen: () => void;
  onEdit: () => void;
  onPrint: () => void;
}> = ({ goal, onOpen, onEdit, onPrint }) => (
  <Box
    onClick={onOpen}
    sx={{
      border: '1px solid #e0e0e0', borderRadius: '8px', p: 1.5,
      backgroundColor: colors.white, cursor: 'pointer',
      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.10)' },
    }}
  >
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', mb: 0.5 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: colors.text, flex: '1 1 200px', minWidth: 0 }}>
        {goal.title || 'Objetivo sem título'}
      </Typography>
      {goal.conclusion && (
        <Chip
          label="Concluído"
          size="small"
          sx={{ height: 22, fontSize: '0.72rem', backgroundColor: '#d4edda', color: '#155724' }}
        />
      )}
      {goal.status && (
        <Chip
          label={goal.status}
          size="small"
          sx={{ height: 22, fontSize: '0.72rem', backgroundColor: colors.backgroundAlt, color: colors.textSecondary }}
        />
      )}
      {goal.priority && (
        <Chip
          label={goal.priority}
          size="small"
          sx={{
            height: 22, fontSize: '0.72rem',
            backgroundColor: `${PRIORIDADE[goal.priority] ?? colors.textSecondary}1f`,
            color: PRIORIDADE[goal.priority] ?? colors.textSecondary,
          }}
        />
      )}
    </Box>

    {goal.goal && <ClampedText text={goal.goal} />}
    <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary, mt: 0.25 }}>
      <strong>Sessões:</strong>{' '}
      {goal.sessionsQuantity
        ? `${goal.sessionsDone} de ${goal.sessionsQuantity} previstas`
        : `${goal.sessionsDone} realizada(s)`}
    </Typography>

    <ScaleBar current={goal.currentScale} target={goal.targetScale} last={goal.lastEvaluation} />

    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.75 }}>
      <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary }}>
        {goal.evaluationCount === 0
          ? 'Nenhuma sessão avaliou este objetivo'
          : `${goal.evaluationCount} sessão(ões) avaliaram · última em ${formatarData(goal.lastEvaluationDate)}`}
      </Typography>
      {goal.professionalNames.length > 0 && (
        <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary }}>
          Responsável: {goal.professionalNames.join(', ')}
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      <Box sx={{ display: 'flex', gap: 0.5 }} onClick={e => e.stopPropagation()}>
        <Tooltip title={goal.conclusion ? 'Objetivo concluído não é editável' : 'Editar objetivo'}>
          <span>
            <IconButton size="small" onClick={onEdit} disabled={goal.conclusion !== null} sx={{ color: colors.primary }}>
              <Edit fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Imprimir o plano">
          <IconButton size="small" onClick={onPrint} sx={{ color: colors.primary }}>
            <Print fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Abrir o objetivo">
          <IconButton size="small" onClick={onOpen} sx={{ color: colors.primary }}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  </Box>
);

const PatientTherapyPlanTab: React.FC<Props> = ({ patientId }) => {
  const [periods, setPeriods] = useState<TherapyPeriodRow[]>([]);
  const [goalsWithoutPeriod, setGoalsWithoutPeriod] = useState<TherapyGoalRow[]>([]);
  const [stages, setStages] = useState<TherapyStageRow[]>([]);
  const [periodOptions, setPeriodOptions] = useState<TherapyFilterOption[]>([]);
  const [teamOptions, setTeamOptions] = useState<TherapyFilterOption[]>([]);
  const [responsibleOptions, setResponsibleOptions] = useState<TherapyFilterOption[]>([]);
  const [professionalOptions, setProfessionalOptions] = useState<TherapyFilterOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [professionalTypeOptions, setProfessionalTypeOptions] = useState<TherapyFilterOption[]>([]);
  const [diagnosisOptions, setDiagnosisOptions] = useState<TherapyFilterOption[]>([]);
  const [priorityOptions, setPriorityOptions] = useState<string[]>([]);
  const [totalGoals, setTotalGoals] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<'lista' | 'quadro'>('quadro');
  const [periodFilter, setPeriodFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [responsibleFilter, setResponsibleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [periodDefaultSet, setPeriodDefaultSet] = useState(false);

  const [periodsModalOpen, setPeriodsModalOpen] = useState(false);
  const [detailGoal, setDetailGoal] = useState<TherapyGoalRow | null>(null);
  const [editGoal, setEditGoal] = useState<TherapyGoalRow | null>(null);
  const [creatingGoal, setCreatingGoal] = useState(false);
  const [reopenedGoalId, setReopenedGoalId] = useState<string | null>(null);
  const [dragGoalId, setDragGoalId] = useState<string | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);

  const { toast, showToast, hideToast } = useToast();

  // No quadro todas as colunas precisam do conjunto inteiro: paginar esconderia cards de fases
  // que nem aparecem na página corrente, e a contagem da coluna mentiria.
  const pageSize = view === 'quadro' ? 100 : PAGE_SIZE;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await patientTherapyPlanService.getByPatient(patientId, {
        periodId: periodFilter,
        status: statusFilter,
        teamId: teamFilter,
        responsibleId: responsibleFilter,
        search,
        pageNumber: view === 'quadro' ? 1 : page,
        pageSize,
      });
      setPeriods(data.periods);
      setGoalsWithoutPeriod(data.goalsWithoutPeriod);
      setStages(data.stages);
      setPeriodOptions(data.periodOptions);
      setTeamOptions(data.teamOptions);
      setResponsibleOptions(data.responsibleOptions);
      setProfessionalOptions(data.professionalOptions);
      setStatusOptions(data.statusOptions);
      setProfessionalTypeOptions(data.professionalTypeOptions);
      setDiagnosisOptions(data.diagnosisOptions);
      setPriorityOptions(data.priorityOptions);
      setTotalGoals(data.totalGoals);
    } catch {
      setError('Não foi possível carregar o plano terapêutico deste paciente.');
    } finally {
      setLoading(false);
    }
  }, [patientId, periodFilter, statusFilter, teamFilter, responsibleFilter, search, page, view, pageSize]);

  // Filtros reativos: a busca espera a digitação parar, o resto responde na hora
  useEffect(() => {
    const t = setTimeout(load, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  // Abre no período mais recente (decisão do Hilton, 02/09), mantendo "Todos" como opção
  useEffect(() => {
    if (!periodDefaultSet && periods.length > 0 && !periodFilter) {
      setPeriodFilter(periods[0].id);
      setPeriodDefaultSet(true);
    }
  }, [periods, periodFilter, periodDefaultSet]);

  const todosObjetivos = useMemo(
    () => [...periods.flatMap(p => p.goals), ...goalsWithoutPeriod],
    [periods, goalsWithoutPeriod]
  );

  // Reabriu a conclusão: assim que o objetivo volta sem ela, o formulário abre nos campos
  useEffect(() => {
    if (!reopenedGoalId) return;
    const alvo = todosObjetivos.find(g => g.id === reopenedGoalId);
    if (alvo && !alvo.conclusion) {
      setEditGoal(alvo);
      setReopenedGoalId(null);
    }
  }, [reopenedGoalId, todosObjetivos]);

  const totalPages = Math.max(1, Math.ceil(totalGoals / PAGE_SIZE));

  const limparFiltros = () => {
    setPeriodFilter('');
    setStatusFilter('');
    setTeamFilter('');
    setResponsibleFilter('');
    setSearch('');
    setPage(1);
    setPeriodDefaultSet(true);
  };

  const temFiltro = periodFilter !== '' || statusFilter !== '' || teamFilter !== ''
    || responsibleFilter !== '' || search !== '';

  const imprimirObjetivo = (goalId: string) => {
    window.open(
      `${window.location.origin}/?page=therapy-goal-print&goalId=${goalId}&patientId=${patientId}`,
      '_blank',
    );
  };

  const trocarFiltro = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPage(1);
  };

  const soltarNaFase = async (stageId: string) => {
    const goalId = dragGoalId;
    setDragGoalId(null);
    setDragOverStageId(null);
    if (!goalId) return;

    const goal = todosObjetivos.find(g => g.id === goalId);
    const stage = stages.find(s => s.id === stageId);
    if (!goal || !stage || goal.status === stage.name) return;

    // Recusa aqui mesmo: sem isso o card iria para a coluna e voltaria depois da resposta
    if (stage.requiresConclusion && !goal.conclusion) {
      showToast(
        `"${stage.name}" só aceita objetivo concluído. Abra o objetivo e registre a conclusão.`,
        'error',
      );
      return;
    }

    try {
      await patientTherapyPlanService.moveGoal(goalId, stageId);
      showToast(`Objetivo movido para "${stage.name}".`, 'success');
      await load();
    } catch (e) {
      // O card volta sozinho: a tela só reflete o que o servidor confirmou
      showToast(therapyPlanErrorMessage(e, 'Não foi possível mover o objetivo.'), 'error');
      await load();
    }
  };

  const renderKanbanCard = (goal: TherapyGoalRow) => (
    <Paper
      key={goal.id}
      elevation={0}
      draggable
      onDragStart={(event) => {
        setDragGoalId(goal.id);
        event.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={() => {
        setDragGoalId(null);
        setDragOverStageId(null);
      }}
      onClick={() => setDetailGoal(goal)}
      sx={{
        p: 1.25,
        borderRadius: '10px',
        border: '1px solid #e9ecef',
        backgroundColor: colors.white,
        cursor: 'grab',
        opacity: dragGoalId === goal.id ? 0.5 : 1,
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.10)' },
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: colors.text, mb: 0.5 }}>
        {goal.title || 'Objetivo sem título'}
      </Typography>

      {goal.periodTitle && (
        <Typography sx={{ fontSize: '0.72rem', color: colors.textSecondary, mb: 0.25 }}>
          {goal.periodTitle}
        </Typography>
      )}
      <Typography sx={{ fontSize: '0.72rem', color: colors.textSecondary, mb: 0.5 }}>
        Sessões: {goal.sessionsQuantity
          ? `${goal.sessionsDone} de ${goal.sessionsQuantity}`
          : goal.sessionsDone}
      </Typography>

      <ScaleBar current={goal.currentScale} target={goal.targetScale} last={goal.lastEvaluation} />

      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', mt: 0.75 }}>
        {goal.priority && (
          <Chip
            label={goal.priority}
            size="small"
            sx={{
              height: 20, fontSize: '0.68rem',
              backgroundColor: `${PRIORIDADE[goal.priority] ?? colors.textSecondary}1f`,
              color: PRIORIDADE[goal.priority] ?? colors.textSecondary,
            }}
          />
        )}
        {goal.evaluationCount > 0 && (
          <Chip
            label={`${goal.evaluationCount} avaliação(ões)`}
            size="small"
            sx={{ height: 20, fontSize: '0.68rem', backgroundColor: colors.backgroundAlt, color: colors.textSecondary }}
          />
        )}
      </Box>
    </Paper>
  );

  const renderKanban = () => (
    <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
      {stages.map(stage => {
        const doStage = todosObjetivos.filter(g => g.status === stage.name);
        return (
          <Box
            key={stage.id}
            onDragOver={(event) => {
              event.preventDefault();
              setDragOverStageId(stage.id);
            }}
            onDragLeave={() => setDragOverStageId(c => (c === stage.id ? null : c))}
            onDrop={() => soltarNaFase(stage.id)}
            sx={{
              minWidth: { xs: 260, sm: 280 },
              maxWidth: 300,
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: dragOverStageId === stage.id ? '#e6f7f9' : '#f1f3f5',
              borderRadius: '12px',
              border: `1px solid ${dragOverStageId === stage.id ? colors.primary : '#e9ecef'}`,
              maxHeight: 'calc(100vh - 380px)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 1.25, pb: 0.75 }}>
              <Box
                sx={{
                  width: 10, height: 10, borderRadius: '50%',
                  backgroundColor: stage.color || colors.primary, flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontWeight: typography.fontWeight.semibold,
                  fontSize: typography.fontSize.sm,
                  color: colors.textPrimary,
                  flex: 1, minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {stage.name}
              </Typography>
              {stage.isFinal && <CheckCircle sx={{ fontSize: 16, color: colors.success }} />}
              {stage.requiresConclusion && (
                <Tooltip title="Só aceita objetivo com conclusão assinada">
                  <Lock sx={{ fontSize: 14, color: colors.textSecondary }} />
                </Tooltip>
              )}
              <Chip
                size="small"
                label={doStage.length}
                sx={{ height: 20, fontSize: '0.7rem', backgroundColor: colors.white }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.25, pt: 0.5, overflowY: 'auto' }}>
              {doStage.length === 0 ? (
                <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, textAlign: 'center', py: 2 }}>
                  {stage.requiresConclusion
                    ? 'Só entram aqui objetivos com conclusão assinada'
                    : temFiltro ? 'Nenhum objetivo com os filtros atuais' : 'Sem objetivos nesta fase'}
                </Typography>
              ) : (
                doStage.map(renderKanbanCard)
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const renderLista = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {periods.map(period => {
        const s = SITUACAO[period.situation] ?? SITUACAO.vigente;
        return (
          <Box key={period.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderLeft: `4px solid ${s.color}`,
                borderRadius: '8px',
                p: 1.5,
                backgroundColor: colors.background,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: colors.text }}>
                  {period.title || 'Período sem título'}
                </Typography>
                <Chip
                  label={s.label}
                  size="small"
                  sx={{ height: 22, fontSize: '0.72rem', backgroundColor: s.bg, color: s.color }}
                />
                <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>
                  {formatarData(period.initialDate)} a {formatarData(period.finalDate)}
                  {` · ${period.goals.length} objetivo(s)`}
                </Typography>
              </Box>
              {period.description && (
                <Box sx={{ mt: 0.5 }}>
                  <ClampedText text={period.description} size="0.8rem" />
                </Box>
              )}
            </Box>

            {period.goals.length === 0 ? (
              <Typography sx={{ fontSize: '0.82rem', color: colors.textSecondary, pl: 0.5, pb: 0.5 }}>
                Nenhum objetivo neste período.
              </Typography>
            ) : (
              period.goals.map(goal => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onOpen={() => setDetailGoal(goal)}
                  onEdit={() => setEditGoal(goal)}
                  onPrint={() => imprimirObjetivo(goal.id)}
                />
              ))
            )}
          </Box>
        );
      })}

      {goalsWithoutPeriod.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderLeft: `4px solid ${colors.textSecondary}`,
              borderRadius: '8px',
              p: 1.5,
              backgroundColor: colors.background,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: colors.text }}>
              Sem período definido
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, mt: 0.25 }}>
              {goalsWithoutPeriod.length} objetivo(s) que não foram agrupados em nenhum período.
            </Typography>
          </Box>
          {goalsWithoutPeriod.map(goal => (
            <GoalCard
              key={goal.id}
              goal={goal}
              onOpen={() => setDetailGoal(goal)}
              onEdit={() => setEditGoal(goal)}
              onPrint={() => imprimirObjetivo(goal.id)}
            />
          ))}
        </Box>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
            size="small"
            siblingCount={0}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <div className="tab-content-section">
      <Box sx={filterBarSx}>
        <TextField
          select
          size="small"
          label="Período"
          value={periodFilter}
          onChange={e => trocarFiltro(setPeriodFilter)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todos os períodos</MenuItem>
          {periodOptions.some(p => p.isShared) && (
            <ListSubheader sx={{ fontSize: '0.72rem', lineHeight: 2, color: colors.textSecondary }}>
              Da clínica
            </ListSubheader>
          )}
          {periodOptions.filter(p => p.isShared).map(p => (
            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
          ))}
          {periodOptions.some(p => !p.isShared) && (
            <ListSubheader sx={{ fontSize: '0.72rem', lineHeight: 2, color: colors.textSecondary }}>
              Deste paciente
            </ListSubheader>
          )}
          {periodOptions.filter(p => !p.isShared).map(p => (
            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Situação"
          value={statusFilter}
          onChange={e => trocarFiltro(setStatusFilter)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todas</MenuItem>
          {statusOptions.map(s => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Equipe"
          value={teamFilter}
          onChange={e => trocarFiltro(setTeamFilter)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todas</MenuItem>
          {teamOptions.map(t => (
            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Responsável"
          value={responsibleFilter}
          onChange={e => trocarFiltro(setResponsibleFilter)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todos</MenuItem>
          {responsibleOptions.map(r => (
            <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label="Buscar"
          placeholder="Objetivo, meta ou métrica"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          inputProps={{ maxLength: 100 }}
          InputLabelProps={{ shrink: true }}
          sx={searchFieldSx}
        />

        <Tooltip title="Limpar filtros">
          <span>
            <IconButton onClick={limparFiltros} disabled={!temFiltro} sx={clearFilterButtonSx}>
              <Clear fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Novo objetivo">
          <IconButton onClick={() => setCreatingGoal(true)} sx={filterActionButtonSx}>
            <Add />
          </IconButton>
        </Tooltip>

        <Tooltip title="Períodos personalizados deste paciente">
          <IconButton onClick={() => setPeriodsModalOpen(true)} sx={filterActionButtonSx}>
            <DateRange />
          </IconButton>
        </Tooltip>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, v) => { if (v) setView(v); }}
          sx={{
            flexShrink: 0,
            '& .MuiToggleButton-root': { height: 40, px: 1.25, borderColor: colors.border },
            '& .Mui-selected': { color: colors.primary },
          }}
        >
          <ToggleButton value="lista" aria-label="Ver em lista">
            <Tooltip title="Lista"><ViewList fontSize="small" /></Tooltip>
          </ToggleButton>
          <ToggleButton value="quadro" aria-label="Ver em quadro">
            <Tooltip title="Quadro"><ViewKanban fontSize="small" /></Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box className="c4u-info-box">
        <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
        <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textPrimary }}>
          Cada objetivo vai de <strong>0 a 10</strong>: o estado atual marca de onde parte e o alvo,
          aonde deve chegar. O atingimento é registrado <strong>nas evoluções das sessões</strong>,
          não nesta tela. No quadro, arrastar um card muda a fase do objetivo.
        </Typography>
      </Box>

      <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, mb: 1 }}>
        {loading
          ? 'Carregando...'
          : `Encontrados: ${totalGoals} ${totalGoals === 1 ? 'item' : 'itens'}${view === 'lista' && totalPages > 1 ? ` · página ${page} de ${totalPages}` : ''}`}
      </Typography>

      {error && (
        <Typography sx={{ color: colors.error, fontSize: typography.fontSize.sm, mb: 2 }}>{error}</Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: colors.primary }} />
        </Box>
      ) : totalGoals === 0 ? (
        <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, py: 3, textAlign: 'center' }}>
          {temFiltro
            ? 'Nenhum objetivo corresponde aos filtros.'
            : 'Nenhum plano terapêutico registrado para este paciente.'}
        </Typography>
      ) : view === 'quadro' ? renderKanban() : renderLista()}

      <TherapyPeriodsModal
        isOpen={periodsModalOpen}
        patientId={patientId}
        periods={periods.filter(p => !p.isShared)}
        professionals={professionalOptions}
        onClose={() => setPeriodsModalOpen(false)}
        onSaved={() => { showToast('Período salvo.', 'success'); load(); }}
        onError={(m) => showToast(m, 'error')}
      />

      <TherapyGoalFormModal
        isOpen={creatingGoal || editGoal !== null}
        goal={editGoal}
        patientId={patientId}
        periods={periodOptions}
        teams={teamOptions}
        professionals={professionalOptions}
        professionalTypes={professionalTypeOptions}
        diagnoses={diagnosisOptions}
        priorities={priorityOptions}
        stages={stages}
        onClose={() => { setCreatingGoal(false); setEditGoal(null); }}
        onSaved={() => { showToast('Objetivo salvo.', 'success'); load(); }}
      />

      <TherapyGoalDetailModal
        goal={detailGoal}
        patientId={patientId}
        onClose={() => setDetailGoal(null)}
        onChanged={() => { showToast('Objetivo atualizado.', 'success'); load(); }}
        onReopened={(id) => {
          setDetailGoal(null);
          setReopenedGoalId(id);
          showToast('Objetivo reaberto para edição.', 'success');
          load();
        }}
        onError={(m) => showToast(m, 'error')}
      />

      <Toast isVisible={toast.isVisible} message={toast.message} type={toast.type} onClose={hideToast} />
    </div>
  );
};

export default PatientTherapyPlanTab;
